import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * AuthActionHandler - A component that processes Firebase auth action links
 * and redirects users to the appropriate page based on the action mode.
 * 
 * This component handles the routing for different Firebase auth actions:
 * - verifyEmail: Email verification links
 * - resetPassword: Password reset links
 * - recoverEmail: Email recovery links
 * - revertSecondFactorAddition: Second factor revert links
 */
const AuthActionHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Extract parameters from URL
    const queryParams = new URLSearchParams(location.search);
    const mode = queryParams.get('mode');
    const oobCode = queryParams.get('oobCode');
    const continueUrl = queryParams.get('continueUrl');
    const lang = queryParams.get('lang');

    console.log('AuthActionHandler - Processing action:', { mode, oobCode: !!oobCode, continueUrl, lang });

    // Validate that we have required parameters
    if (!mode || !oobCode) {
      setError('Invalid action link. Missing required parameters.');
      setIsProcessing(false);
      return;
    }

    // Build query string for redirection
    const params = new URLSearchParams();
    params.append('mode', mode);
    params.append('oobCode', oobCode);
    if (continueUrl) params.append('continueUrl', continueUrl);
    if (lang) params.append('lang', lang);

    // Route to appropriate handler based on mode
    switch (mode) {
      case 'verifyEmail':
        console.log('Redirecting to email verification page');
        navigate(`/verify-email?${params.toString()}`, { replace: true });
        break;
        
      case 'resetPassword':
        console.log('Redirecting to password reset page');
        navigate(`/reset-password?${params.toString()}`, { replace: true });
        break;
        
      case 'recoverEmail':
        console.log('Redirecting to email recovery page');
        // For now, redirect to login with an appropriate message
        navigate('/login?message=email-recovery', { replace: true });
        break;
        
      case 'revertSecondFactorAddition':
        console.log('Redirecting to two-factor revert page');
        // For now, redirect to login with an appropriate message  
        navigate('/login?message=two-factor-revert', { replace: true });
        break;
        
      default:
        console.error('Unknown auth action mode:', mode);
        setError(`Unknown action type: ${mode}. Please contact support if this continues.`);
        setIsProcessing(false);
        return;
    }
  }, [location, navigate]);

  if (isProcessing) {
    return (
      <div className="auth-container">
        <div className="auth-content">
          <div className="auth-card">
            <h2 className="auth-title">Processing Action</h2>
            <div className="verification-loading">
              <p>Processing your request...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-container">
        <div className="auth-content">
          <div className="auth-card">
            <h2 className="auth-title">Action Failed</h2>
            <div className="auth-error">{error}</div>
            <button 
              onClick={() => navigate('/login')} 
              className="auth-button"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthActionHandler;
